import { useEffect, useState } from 'react';
import {
  GRADES, RULES, TARGETED_RULES, getGrade, isHunt, resolveConfig, ruleKind,
  type Celebration, type Grade, type LevelConfig, type MultipleDisplay, type RuleId,
} from '@numslearn/shared';
import { useSettings, type ModeChoice, type Preset, type TextSize } from '../settings.js';
import type { Lang, Numerals, StringKey } from '../i18n/index.js';
import { NumberField, Section, Segmented, Select, Toggle } from './controls.js';
import { MultiplierField, RangeFields } from './levelFields.js';
import { ruleLine } from '../game/verdict.js';
import { ThemePicker } from './ThemePicker.js';
import { PicturePicker } from './PicturePicker.js';
import { usePresets } from './usePresets.js';
import { useMedia } from '../media.js';
import { MediaManager } from './MediaManager.js';
import { CurriculumEditor } from './CurriculumEditor.js';

const RULE_IDS = Object.keys(RULES) as RuleId[];
const PAIR_RULES = RULE_IDS.filter((r) => ruleKind(r) === 'pair');
const HUNT_RULES = RULE_IDS.filter((r) => ruleKind(r) === 'hunt');

/** Which label the target field wears depends on what the target means. */
function targetLabelKey(rule: RuleId): StringKey {
  if (rule === 'productTo') return 'admin.targetProduct';
  if (rule === 'multiplesOf') return 'admin.targetMultiple';
  if (rule === 'factorsOf') return 'admin.targetFactor';
  return 'admin.target';
}

export function AdminPanel({ onClose }: { onClose: () => void }) {
  const { settings, set, setOverride, resetOverrides, config, t, num } = useSettings();
  const [presetName, setPresetName] = useState('');
  const presets = usePresets(settings.presets, (next) => set('presets', next));
  const media = useMedia();
  const { refresh: refreshMedia } = media;
  // The teacher may have signed in or out in another tab since the app loaded.
  useEffect(() => { void refreshMedia(); }, [refreshMedia]);
  const [editingCurriculum, setEditingCurriculum] = useState(false);
  const hunt = isHunt(config);
  /** The shared level fields speak in patches; the panel stores per-key overrides. */
  const patch = (p: Partial<LevelConfig>) => {
    for (const [k, v] of Object.entries(p)) setOverride(k as keyof LevelConfig, v as never);
  };

  const grade = getGrade(settings.grade);

  const savePreset = () => {
    const name = presetName.trim();
    if (!name) return;
    const preset: Preset = {
      id: `${Date.now()}`,
      name,
      grade: settings.grade,
      level: settings.level,
      overrides: settings.overrides,
      theme: settings.themeChoice,
    };
    presets.save(preset);
    setPresetName('');
  };

  const loadPreset = (p: Preset) => {
    set('grade', p.grade);
    set('level', p.level);
    set('overrides', p.overrides);
    set('themeChoice', p.theme);
  };

  return (
    <div className="admin" role="dialog" aria-modal="true" aria-label={t('admin.title')}>
      <header className="admin-hd">
        <div>
          <h1>{t('admin.title')}</h1>
          <p>{t('admin.subtitle')}</p>
        </div>
        <button className="a-close" onClick={onClose}>{t('btn.done')}</button>
      </header>

      <div className="admin-body">
        <Section title={t('admin.theme')} help={t('admin.themeHelp')}>
          <ThemePicker />
        </Section>

        <Section title={t('admin.appearance')}>
          <div className="a-row">
            <Segmented<ModeChoice>
              label={t('admin.mode')}
              value={settings.mode}
              onChange={(v) => set('mode', v)}
              options={[
                { value: 'light', label: t('admin.modeLight') },
                { value: 'dark', label: t('admin.modeDark') },
                { value: 'auto', label: t('admin.modeAuto') },
              ]}
            />
            <Segmented<Lang>
              label={t('admin.lang')}
              value={settings.lang}
              onChange={(v) => set('lang', v)}
              options={[{ value: 'ar', label: 'العربية' }, { value: 'en', label: 'English' }]}
            />
            <Segmented<Numerals>
              label={t('admin.numerals')}
              value={settings.numerals}
              onChange={(v) => set('numerals', v)}
              options={[
                { value: 'latn', label: t('admin.numLatin') },
                { value: 'arab', label: t('admin.numArabic') },
              ]}
            />
          </div>
          <Toggle
            label={t('admin.sound')}
            hint={t('admin.soundHelp')}
            checked={settings.sound}
            onChange={(v) => set('sound', v)}
          />
        </Section>

        <Section title={t('admin.content')}>
          <div className="a-row">
            <Select<Grade>
              label={t('admin.grade')}
              value={settings.grade}
              onChange={(v) => { set('grade', v); set('level', 1); }}
              options={GRADES.map((g) => ({
                value: g.grade,
                label: `${t('msg.grade', { g: g.grade })} — ${t(g.ruleKey as StringKey, { n: resolveConfig(g.grade, 1).ruleTarget })}`,
              }))}
            />
            <Select<number>
              label={t('admin.sublevel')}
              value={settings.level}
              onChange={(v) => set('level', v)}
              options={grade.levels.map((l) => ({
                value: l.index,
                label: `${t('msg.level', { n: l.index })} — ${t(l.nameKey as StringKey)}`,
              }))}
            />
          </div>
        </Section>

        <Section title={t('admin.rules')}>
          <div className="a-row">
            <Segmented<'pair' | 'hunt'>
              label={t('admin.mechanic')}
              value={hunt ? 'hunt' : 'pair'}
              onChange={(v) => setOverride('rule', v === 'hunt' ? 'even' : 'sumTo')}
              options={[
                { value: 'pair', label: t('admin.mechanicPair') },
                { value: 'hunt', label: t('admin.mechanicHunt') },
              ]}
            />
          </div>
          <div className="a-row">
            <Select<RuleId>
              label={t('admin.rule')}
              value={config.rule}
              onChange={(v) => setOverride('rule', v)}
              options={(hunt ? HUNT_RULES : PAIR_RULES).map((r) => ({
                value: r, label: ruleLine({ ...config, rule: r }, t),
              }))}
            />
            {TARGETED_RULES.has(config.rule) && (
              <NumberField
                label={t(targetLabelKey(config.rule))} value={config.ruleTarget} min={2} max={144}
                onChange={(v) => setOverride('ruleTarget', v)}
              />
            )}
          </div>
          {hunt ? (
            <NumberField
              label={t('admin.density')}
              hint={t('admin.densityHelp')}
              value={Math.round(config.huntDensity * 100)} min={15} max={80} step={5}
              onChange={(v) => setOverride('huntDensity', v / 100)}
            />
          ) : (
            <Toggle
              label={t('admin.adjacency')}
              hint={t('admin.adjacencyHelp')}
              checked={config.adjacency}
              onChange={(v) => setOverride('adjacency', v)}
            />
          )}
          {config.rule === 'multipleOf' && (
            <>
              <Segmented<MultipleDisplay>
                label={t('admin.multipleDisplay')}
                value={config.multipleDisplay}
                onChange={(v) => setOverride('multipleDisplay', v)}
                options={[
                  { value: 'factor', label: `${num(3)} × ${num(3)} = ${num(9)}` },
                  { value: 'division', label: `${num(9)} ÷ ${num(3)} = ${num(3)}` },
                ]}
              />
              <Toggle
                label={t('admin.equalMultiples')} checked={config.allowEqualMultiples}
                onChange={(v) => setOverride('allowEqualMultiples', v)}
              />
              <Toggle
                label={t('admin.oneFactor')} checked={config.allowOneAsFactor}
                onChange={(v) => setOverride('allowOneAsFactor', v)}
              />
            </>
          )}
          <MultiplierField config={config} onChange={patch} />
          <RangeFields config={config} onChange={patch} />
        </Section>

        <Section title={t('admin.presentation')}>
          <Segmented<TextSize>
            label={t('admin.textSize')}
            hint={t('admin.textSizeHelp')}
            value={settings.textSize}
            onChange={(v) => set('textSize', v)}
            options={[
              { value: 'normal', label: t('admin.textNormal') },
              { value: 'large', label: t('admin.textLarge') },
              { value: 'huge', label: t('admin.textHuge') },
              { value: 'projector', label: t('admin.textProjector') },
            ]}
          />
          <Segmented<Celebration>
            label={t('admin.celebration')}
            hint={t('admin.celebrationHelp')}
            value={config.celebration}
            onChange={(v) => setOverride('celebration', v)}
            options={[
              { value: 'sparks', label: t('admin.celebrationSparks') },
              { value: 'word', label: t('admin.celebrationWord') },
              { value: 'full', label: t('admin.celebrationFull') },
            ]}
          />
          <Toggle
            label={t('admin.decorations')}
            hint={t('admin.decorationsHelp')}
            checked={config.decorations}
            onChange={(v) => setOverride('decorations', v)}
          />
          <Toggle
            label={t('admin.puzzle')}
            hint={t('admin.puzzleHelp')}
            checked={config.puzzle}
            onChange={(v) => setOverride('puzzle', v)}
          />
          {config.puzzle && (
            <PicturePicker
              value={config.puzzleScene}
              onChange={(v) => setOverride('puzzleScene', v)}
            />
          )}
        </Section>

        <Section title={t('admin.board')}>
          <div className="a-row">
            <NumberField label={t('admin.rows')} value={config.rows} min={2} max={12} onChange={(v) => setOverride('rows', v)} />
            <NumberField label={t('admin.cols')} value={config.cols} min={2} max={12} onChange={(v) => setOverride('cols', v)} />
          </div>
          <div className="a-row">
            <NumberField
              label={t('admin.timer')} value={config.timerSec ?? 0} min={0} max={600} step={5}
              hint={config.timerSec === null ? t('admin.timerOff') : undefined}
              onChange={(v) => setOverride('timerSec', v === 0 ? null : v)}
            />
            <NumberField label={t('admin.adds')} value={config.adds} min={0} max={9} onChange={(v) => setOverride('adds', v)} />
            <NumberField label={t('admin.hints')} value={config.hints} min={0} max={9} onChange={(v) => setOverride('hints', v)} />
            <NumberField label={t('admin.penalty')} value={config.missPenalty} min={0} max={50} onChange={(v) => setOverride('missPenalty', v)} />
          </div>
          <Toggle
            label={t('admin.solvable')} checked={config.guaranteeSolvable}
            onChange={(v) => setOverride('guaranteeSolvable', v)}
          />
          <button className="a-reset" onClick={resetOverrides} disabled={Object.keys(settings.overrides).length === 0}>
            {t('btn.reset')}
            {Object.keys(settings.overrides).length > 0 && ` (${num(Object.keys(settings.overrides).length)})`}
          </button>
        </Section>

        {/* A curriculum is saved to a teacher account, so it is offered only
            to a signed-in teacher — practice mode has nowhere to put it. */}
        {media.available && (
          <Section title={t('curriculum.title')} help={t('curriculum.subtitle')}>
            <button className="a-reset" onClick={() => setEditingCurriculum(true)}>
              {t('curriculum.edit')}
            </button>
          </Section>
        )}

        {/* Pictures can always be kept — on the account, or in this browser. */}
        {media.canUploadPictures && (
          <Section title={t('media.title')} help={t('media.subtitle')}>
            <MediaManager />
          </Section>
        )}

        <Section title={t('admin.presets')}>
          <div className="a-row">
            <label className="a-field">
              <span className="a-label">{t('admin.presetName')}</span>
              <input
                className="a-select" type="text" value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder={t('admin.presetPlaceholder')}
              />
            </label>
            <button className="a-reset" onClick={savePreset} disabled={!presetName.trim()}>{t('admin.save')}</button>
          </div>
          <p className="a-empty">{t(presets.synced ? 'admin.presetsServer' : 'admin.presetsLocal')}</p>
          {presets.list.length === 0 ? (
            <p className="a-empty">{t('admin.noPresets')}</p>
          ) : (
            <ul className="preset-list">
              {presets.list.map((p) => (
                <li key={p.id}>
                  <span>
                    <b>{p.name}</b>
                    <small>{t('msg.grade', { g: p.grade })} · {t('msg.level', { n: p.level })}</small>
                  </span>
                  <span className="preset-acts">
                    <button onClick={() => loadPreset(p)}>{t('admin.load')}</button>
                    <button onClick={() => presets.remove(p.id)}>{t('admin.delete')}</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {editingCurriculum && <CurriculumEditor onClose={() => setEditingCurriculum(false)} />}
    </div>
  );
}
